import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Lock, User, Shield, CheckCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useForcedDarkMode } from '../contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignupPage = () => {
  // Force dark mode on signup page
  useForcedDarkMode();
  
  const [formData, setFormData] = useState({ name: '', email: '', password: '', company_name: '' });
  const [loading, setLoading] = useState(false);
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: null });
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();

  // Get tier and module from URL params
  const tier = searchParams.get('tier') || 'standard';
  const module = searchParams.get('module');
  const modules = searchParams.get('modules');

  // Load CAPTCHA configuration
  useEffect(() => {
    const loadCaptchaConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/v2/captcha-config`);
        setCaptchaConfig(response.data);
        
        if (response.data.enabled && response.data.site_key) {
          // Load reCAPTCHA v3 script
          const script = document.createElement('script');
          script.src = `https://www.google.com/recaptcha/api.js?render=${response.data.site_key}`;
          script.async = true;
          script.onload = () => setCaptchaLoaded(true);
          document.head.appendChild(script);
        }
      } catch (error) {
      }
    };
    
    loadCaptchaConfig();
  }, []);

  // Get CAPTCHA token
  const getCaptchaToken = useCallback(async () => {
    if (!captchaConfig.enabled || !captchaLoaded || !window.grecaptcha) {
      return null;
    }
    
    try {
      const token = await window.grecaptcha.execute(captchaConfig.site_key, { action: 'signup' });
      return token;
    } catch (error) {
      console.error('CAPTCHA error:', error);
      return null;
    }
  }, [captchaConfig, captchaLoaded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get CAPTCHA token if enabled
      const captchaToken = await getCaptchaToken();
      
      // Determine signup source from URL params
      // full_platform, grc, pmo, contracts, cosec, ma
      let signupSource = 'grc'; // Default to GRC (full trial)
      if (modules === 'full' || tier === 'pro') {
        signupSource = 'full_platform';
      } else if (module) {
        signupSource = module.toLowerCase();
      }
      
      const payload = {
        ...formData,
        captcha_token: captchaToken,
        signup_source: signupSource
      };
      
      const response = await axios.post(`${API}/auth/v2/signup`, payload);
      localStorage.setItem('session_token', response.data.session_token);
      setUser(response.data.user);
      
      // Trial is now automatically activated - go straight to onboarding
      // User has 7 days free access based on their signup_source
      toast.success('Welcome! Your 7-day trial has started.');
      
      // Set flag to show trial banner and trigger onboarding
      localStorage.setItem('simplifi_needs_onboarding', 'true');
      localStorage.setItem('simplifi_trial_started', new Date().toISOString());
      
      navigate('/smart-onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="signup-page">
      <Helmet>
        <title>Start Free Trial | GRC & Business Management Software | Simplif-i</title>
        <meta name="description" content="Create your free Simplif-i account. Access ISO 27001, SOC 2, GDPR compliance tools, PMO, contract management and more. No credit card required. 14-day free trial." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://simplif-i.com/signup" />
        <meta name="keywords" content="free trial, GRC software signup, compliance software trial, ISO 27001 software free, business management platform" />
      </Helmet>

      {/* Left Panel - Benefits (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D1117] flex-col justify-center p-12">
        <div className="max-w-md mx-auto">
          <Logo className="h-10 mb-8" textClassName="text-white" />
          
          <h2 className="text-3xl font-bold text-white mb-6">
            Start your free trial today
          </h2>
          
          <p className="text-white/70 text-lg mb-8">
            Join thousands of businesses using Simplif-i to manage compliance, governance, and operations in one connected platform.
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-[#2D7D6F] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">14-day free trial</p>
                <p className="text-white/60 text-sm">Full access to all features. No credit card required.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#2D7D6F] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">30+ compliance frameworks</p>
                <p className="text-white/60 text-sm">ISO 27001, SOC 2, GDPR, Cyber Essentials, HIPAA and more.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-[#2D7D6F] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">AI-powered automation</p>
                <p className="text-white/60 text-sm">Policy generation, evidence mapping, and questionnaire completion.</p>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-white/50 text-sm">Trusted by compliance teams worldwide</p>
            <p className="text-white/70 text-sm mt-2">Fast-growing startups, SMEs, and enterprise organisations across UK, Europe, and US rely on Simplif-i for their compliance and governance needs.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex justify-center mb-6 lg:hidden">
              <Logo className="h-12" />
            </Link>
            <h1 className="text-3xl font-semibold mb-2 font-display" data-testid="signup-title">Create account</h1>
            <p className="text-muted-foreground font-body">Start managing your compliance frameworks with a 14-day free trial</p>
          </div>

          <div className="border border-border rounded-lg p-8 bg-card">
          <Button 
            variant="outline" 
            className="w-full mb-6 h-11" 
            onClick={handleGoogleLogin}
            type="button"
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="signup-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="company_name">Company Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <div className="relative mt-1.5">
                  <svg className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <Input
                    id="company_name"
                    type="text"
                    placeholder="Acme Ltd"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="pl-10"
                    data-testid="company-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-11 bg-[#2D7D6F] text-white hover:bg-[#236B5F] font-semibold" disabled={loading} data-testid="signup-submit-btn">
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </div>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6 font-body">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2D7D6F] hover:underline font-semibold" data-testid="login-link">
              Sign in
            </Link>
          </p>
          
          {captchaConfig.enabled && (
            <p className="text-center text-xs text-muted-foreground mt-4 opacity-60">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">
                Privacy Policy
              </a>{' '}and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">
                Terms of Service
              </a>{' '}apply.
            </p>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;